import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import { salApi, segnalazioniApi } from '../api';
import PulsanteSegnala from './PulsanteSegnala';
import CambiaPassword from './CambiaPassword';

const navBase = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/clienti', label: 'Clienti', icon: '🏢' },
  { to: '/formazione', label: 'Scadenziario formativo', icon: '🎓' },
  { to: '/template-offerte', label: 'Template Offerte', icon: '📐' },
  { to: '/tipologie-offerte', label: 'Tipologie Offerte', icon: '🏷' },
  { label: 'Vendite', header: true },
  { to: '/offerte', label: 'Offerte', icon: '📄' },
  { to: '/commesse', label: 'Commesse', icon: '📁' },
  { to: '/fatturazioni', label: 'Fatturazione', icon: '💶' },
  { label: 'Consulenti', header: true },
  { to: '/consulenti', label: 'Consulenti', icon: '👤' },
  { to: '/offerte-consulenti', label: 'Offerte Consulenti', icon: '📋' },
  { to: '/costi-consulenti', label: 'Costi Imputati', icon: '💰' },
  { to: '/fatture-consulenti', label: 'Fatture Consulenti', icon: '🧾' },
  { label: 'Collaborazione', header: true },
  { to: '/sal', label: 'SAL / Avanzamenti', icon: '📈', badge: 'sal' },
  { to: '/guida', label: 'Come funziona', icon: '📖' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [salPendenti, setSalPendenti] = useState(0);
  const [segnalazioniAperte, setSegnalazioniAperte] = useState(0);
  const [cambioPassword, setCambioPassword] = useState(false);
  const nav = user?.ruolo === 'admin'
    ? [...navBase,
       { to: '/utenti', label: 'Utenti e Accessi', icon: '🔐' },
       { to: '/segnalazioni', label: 'Segnalazioni', icon: '💬', badge: 'segnalazioni' }]
    : navBase;

  useEffect(() => {
    // Stesso criterio della vista "Da approvare": gli inviati, più le bozze
    // create da noi (che non passano da un invio). Le bozze dei consulenti no:
    // sono ancora in stesura.
    const conta = () => salApi.list({})
      .then(rows => setSalPendenti(rows.filter(s =>
        s.stato === 'inviato' || (s.stato === 'bozza' && s.utente_ruolo !== 'esterno')).length))
      .catch(() => {});
    conta();
    const t = setInterval(conta, 30000);
    return () => clearInterval(t);
  }, []);

  // Le segnalazioni aperte restavano invisibili finché non si apriva la pagina:
  // il numero sta accanto alla voce, così si vede entrando.
  useEffect(() => {
    if (user?.ruolo !== 'admin') return;
    const conta = () => segnalazioniApi.list()
      .then(d => setSegnalazioniAperte(
        (d?.conteggi?.nuova || 0) + (d?.conteggi?.presa_in_carico || 0)))
      .catch(() => {});
    conta();
    const t = setInterval(conta, 120000);
    return () => clearInterval(t);
  }, [user?.ruolo]);

  return (
    <aside className="w-56 bg-brand-900 text-white flex flex-col shrink-0">
      {/* Marchio nella versione NEGATIVA, disegnata per i fondi scuri: niente
          più filtri sul bianco né ritagli calcolati sulle coordinate del file.
          È orizzontale (1916x417), quindi occupa poca altezza e lascia spazio
          al menu. */}
      <div className="px-4 py-4 border-b border-white/10">
        <img src="/logo-negativo.png" alt="Sinthema" className="w-[150px] h-auto block" />
        <div className="text-[10px] text-brand-200/70 uppercase tracking-[0.22em] mt-2 pl-0.5">
          Gestionale
        </div>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {nav.map((item, i) =>
          item.header ? (
            <div key={i} className="px-2 pt-4 pb-1 text-xs font-semibold text-brand-300 uppercase tracking-wider">
              {item.label}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-brand-600 text-white font-medium' : 'text-brand-100 hover:bg-brand-700'
                }`
              }
            >
              <span>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.badge === 'sal' && salPendenti > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {salPendenti}
                </span>
              )}
              {item.badge === 'segnalazioni' && segnalazioniAperte > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {segnalazioniAperte}
                </span>
              )}
            </NavLink>
          )
        )}
      </nav>
      <div className="px-2 py-2 border-t border-brand-700 space-y-1">
        {/* Sempre a portata, da qualunque schermata: una segnalazione si scrive
            nel momento in cui il problema capita, non tornando indietro dopo. */}
        <PulsanteSegnala />
        <div className="px-2 pt-1">
          <div className="text-xs text-brand-200 mb-1 truncate">{user?.nome_completo || user?.nome}</div>
          <div className="flex gap-3">
            <button onClick={() => setCambioPassword(true)}
                    className="text-xs text-brand-300 hover:text-white underline">Cambia password</button>
            <button onClick={logout} className="text-xs text-brand-300 hover:text-white underline">Esci</button>
          </div>
          {cambioPassword && <CambiaPassword onClose={() => setCambioPassword(false)} />}
        </div>
      </div>
    </aside>
  );
}
